/**
 * SAYM wishlist ("gem produkt").
 *
 * Saved products live in localStorage as a list of product handles, so the list
 * survives reloads without an account or an app. It is per browser: it does not
 * follow the customer to another device. Swapping this for a real account-backed
 * wishlist later only means replacing the read/write in SaymWishlistStore.
 *
 * Elements:
 *   <saym-wishlist-toggle handle="..." title="...">  heart button on cards and the product page
 *   <saym-wishlist-count>                            number badge, hidden while empty
 *   <saym-wishlist-list>                             drawer body, renders the saved cards
 */

const STORAGE_KEY = 'saym:wishlist';
const CHANGED = 'saym:wishlist:change';

const SaymWishlistStore = {
  /** @returns {string[]} saved product handles, oldest first */
  read() {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(raw) ? raw.filter((h) => typeof h === 'string' && h) : [];
    } catch {
      // Private mode, blocked storage or corrupted value: behave like an empty list.
      return [];
    }
  },

  /** @param {string[]} handles */
  write(handles) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch {
      // Nothing to do: the toggle still reflects the change for this page view.
    }
    document.dispatchEvent(new CustomEvent(CHANGED, { detail: { handles } }));
  },

  /** @param {string} handle */
  has(handle) {
    return this.read().includes(handle);
  },

  /** @param {string} handle @returns {boolean} true when it is now saved */
  toggle(handle) {
    const handles = this.read();
    const index = handles.indexOf(handle);
    if (index > -1) handles.splice(index, 1);
    else handles.push(handle);
    this.write(handles);
    return index === -1;
  },
};

class SaymWishlistToggle extends HTMLElement {
  connectedCallback() {
    this.handle = this.getAttribute('handle');
    this.button = this.querySelector('button');
    if (!this.handle || !this.button) return;

    this.button.addEventListener('click', this.#onClick);
    this.onChange = () => this.#render();
    document.addEventListener(CHANGED, this.onChange);
    this.#render();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
    if (this.onChange) document.removeEventListener(CHANGED, this.onChange);
  }

  #onClick = (/** @type {MouseEvent} */ event) => {
    // Cards wrap the whole tile in a link to the product.
    event.preventDefault();
    event.stopPropagation();
    if (this.handle) SaymWishlistStore.toggle(this.handle);
  };

  #render() {
    if (!this.handle || !this.button) return;
    const saved = SaymWishlistStore.has(this.handle);
    this.button.setAttribute('aria-pressed', String(saved));
    this.toggleAttribute('saved', saved);
    const label = saved ? this.dataset.labelRemove : this.dataset.labelAdd;
    if (label) this.button.setAttribute('aria-label', label);
  }
}

class SaymWishlistCount extends HTMLElement {
  connectedCallback() {
    this.onChange = () => this.#render();
    document.addEventListener(CHANGED, this.onChange);
    this.#render();
  }

  disconnectedCallback() {
    if (this.onChange) document.removeEventListener(CHANGED, this.onChange);
  }

  #render() {
    const count = SaymWishlistStore.read().length;
    this.textContent = String(count);
    this.toggleAttribute('hidden', count === 0);
  }
}

class SaymWishlistList extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.getAttribute('section-id');
    this.dialog = this.closest('dialog');

    this.onChange = () => {
      this.dirty = true;
      if (this.#isOpen()) this.render();
    };
    document.addEventListener(CHANGED, this.onChange);

    // The drawer keeps its markup in the DOM and only toggles the dialog's
    // `open` attribute, so that is the reliable signal that it became visible.
    if (this.dialog) {
      this.observer = new MutationObserver(() => {
        if (this.#isOpen() && this.dirty) this.render();
      });
      this.observer.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
    }

    this.dirty = true;
    if (this.#isOpen()) this.render();
    else this.#renderEmptyState();
  }

  disconnectedCallback() {
    if (this.onChange) document.removeEventListener(CHANGED, this.onChange);
    this.observer?.disconnect();
  }

  #isOpen() {
    return this.dialog ? this.dialog.hasAttribute('open') : true;
  }

  /** Show or hide the empty state without paying for any fetch. */
  #renderEmptyState() {
    const handles = SaymWishlistStore.read();
    this.querySelector('[data-wishlist-empty]')?.toggleAttribute('hidden', handles.length > 0);
    this.querySelector('[data-wishlist-grid]')?.toggleAttribute('hidden', handles.length === 0);
  }

  async render() {
    const handles = SaymWishlistStore.read();
    const grid = this.querySelector('[data-wishlist-grid]');
    const empty = this.querySelector('[data-wishlist-empty]');
    if (!grid || !empty) return;

    this.dirty = false;
    empty.toggleAttribute('hidden', handles.length > 0);
    grid.toggleAttribute('hidden', handles.length === 0);
    if (!handles.length) {
      grid.innerHTML = '';
      return;
    }

    this.setAttribute('loading', '');
    // Newest first reads better than insertion order in a saved list.
    const cards = await Promise.all([...handles].reverse().map((handle) => this.#card(handle)));
    // Deliberately not pruning handles that failed to render: a network blip
    // would silently wipe the visitor's saved products.
    grid.innerHTML = cards.filter(Boolean).join('');
    this.removeAttribute('loading');
  }

  /** @param {string} handle */
  async #card(handle, retry = true) {
    const root = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=${this.sectionId}`);
      if (!response.ok) throw new Error(String(response.status));
      const html = await response.text();
      // The Section Rendering API returns the section wrapper; keep its contents.
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const inner = parsed.querySelector('[data-saym-wishlist-card]');
      return inner ? `<li>${inner.innerHTML}</li>` : '';
    } catch {
      if (retry) return this.#card(handle, false);
      return '';
    }
  }
}

if (!customElements.get('saym-wishlist-toggle')) customElements.define('saym-wishlist-toggle', SaymWishlistToggle);
if (!customElements.get('saym-wishlist-count')) customElements.define('saym-wishlist-count', SaymWishlistCount);
if (!customElements.get('saym-wishlist-list')) customElements.define('saym-wishlist-list', SaymWishlistList);
